import React from 'react';
import { formatDate } from '@gloat/time';
import Button from '../button/button';
import Input, { InputContainer } from '../input';
import Popover from '../popover/popover';
import Panel from '../panel/panel';
import ButtonGroup from '../group/button-group';
import { RoundButton } from '../button';
import { InputProps } from '../input/input';
import { Div } from '../box';
import Spinner from '../spinner';
import DatePicker, { DatePickerProps } from './date-picker';

export interface InputDatePickerProps extends DatePickerProps {
  buttonOkText?: string;
  buttonCancelText?: string;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  displayFormat?: string;
  side?: 'left' | 'right' | 'top' | 'bottom';
  'aria-describedby'?: InputProps['aria-describedby'];
  'aria-invalid'?: InputProps['aria-invalid'];
  'aria-label'?: string;
  onBlur?: InputProps['onBlur'];
  'data-testid'?: string;
  loading?: boolean;
}

const InputDatePicker = ({
  value,
  displayFormat = 'MMM D[, ]YYYY',
  onChange,
  buttonOkText = 'Ok',
  buttonCancelText = 'Cancel',
  disabled,
  id,
  loading,
  placeholder,
  side = 'right',
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  onBlur,
  'data-testid': dataTestId,
  ...rest
}: InputDatePickerProps) => {
  const [datePickerValue, setDatePickerValue] = React.useState(() => {
    if (rest.mode === 'single-month-year' && !value) {
      return new Date();
    }

    return value;
  });

  return (
    <Popover>
      {({ isOpen, setIsOpen, hide }) => {
        const onClick = isOpen ? undefined : () => setIsOpen(!isOpen);

        return (
          <>
            <Popover.Trigger disabled={disabled}>
              <div data-testid={dataTestId}>
                <InputContainer icons={['right']}>
                  <Input
                    data-testid={'date-picker-input'}
                    placeholder={placeholder}
                    className={'date-picker-input'}
                    focused={isOpen}
                    value={value ? formatDate({ date: value, format: displayFormat }) : ''}
                    disabled={disabled}
                    id={id}
                    ariaLabel={ariaLabel || placeholder}
                    aria-required={rest.required}
                    onBlur={onBlur}
                    aria-invalid={ariaInvalid}
                    aria-describedby={ariaDescribedBy}
                  />
                  <InputContainer.RightIconContainer>
                    {disabled ? (
                      <InputContainer.Icon
                        name={'CALENDAR_TODAY'}
                        size={'md'}
                        variant={'gray400'}
                        disabled={disabled}
                      />
                    ) : (
                      <RoundButton
                        active={isOpen}
                        onClick={onClick}
                        variant={'secondary-clean'}
                        size={'sm'}
                        aria-label={'open calendar'}
                        data-testid={'date-picker-open-button'}
                      >
                        {loading ? (
                          <Spinner size={'xs'} />
                        ) : (
                          <InputContainer.Icon name={'CALENDAR_TODAY'} size={'md'} variant={'inherit'} />
                        )}
                      </RoundButton>
                    )}
                  </InputContainer.RightIconContainer>
                </InputContainer>
              </div>
            </Popover.Trigger>

            <Popover.Content side={side} size={'auto'}>
              <Div padding={'sm'}>
                <DatePicker value={datePickerValue} onChange={setDatePickerValue} {...rest} />
              </Div>

              <Panel
                display={'flex'}
                padding={'xs'}
                alignItems={'center'}
                justifyContent={'flex-end'}
                variant={'gray100'}
              >
                <ButtonGroup>
                  <Button size={'md'} variant={'secondary-clean'} onClick={hide} data-name={'cancel-btn'}>
                    {buttonCancelText}
                  </Button>
                  <Button
                    size={'md'}
                    disabled={!datePickerValue}
                    className={'text-capitalize'}
                    data-name={'ok-btn'}
                    onClick={() => {
                      onChange?.(datePickerValue!);
                      hide();
                    }}
                  >
                    {buttonOkText}
                  </Button>
                </ButtonGroup>
              </Panel>
            </Popover.Content>
          </>
        );
      }}
    </Popover>
  );
};

export default InputDatePicker;
